import { useState, type FormEvent } from 'react'
import { ScreenShell } from '@/components/layout/ScreenShell'
import { GameButton } from '@/components/ui/GameButton'
import { PixelIcon } from '@/components/ui/PixelIcon'
import { useAuth } from '@/arcade'
import { useProgress } from '@/contexts/ProgressProvider'
import { computeGraceScore } from '@/game/scoring'
import { ACHIEVEMENTS } from '@/game/achievements'
import { getCharacter, CHARACTERS } from '@/game/characters'
import { characterArt } from '@/game/art'
import { ArtImage } from '@/components/ui/ArtImage'

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="min-w-0 rounded-[var(--r-md)] bg-[var(--surface)] shadow-emboss border border-[var(--edge)]/30 px-3 py-3 text-center">
      <div className="font-display text-xl font-black tabular-nums">{value}</div>
      <div className="text-xs text-[var(--text-dim)]">{label}</div>
    </div>
  )
}

export function ProfileScreen() {
  const { user, isSignedIn, busy, error, login, register, logout } = useAuth()
  const { save } = useProgress()
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    try {
      if (mode === 'login') await login(username.trim(), password)
      else await register(username.trim(), password)
      setPassword('')
    } catch {
      /* error surfaced via hook */
    }
  }

  const totalStars = Object.values(save.progress.levelStars).reduce((a: number, b) => a + b, 0)
  const equippedChar = getCharacter(save.cosmetics.equippedCharacter) ?? CHARACTERS[0]
  const equippedArt = characterArt(equippedChar.id)

  return (
    <ScreenShell title="Profile">
      <div className="flex flex-col gap-5">
        {isSignedIn ? (
          <div className="rounded-[var(--r-lg)] bg-[var(--surface)] shadow-emboss border border-[var(--edge)]/40 p-4 flex items-center gap-3">
            <div
              className="relative h-14 w-14 shrink-0 overflow-hidden rounded-[var(--r-md)]"
              style={{ background: `radial-gradient(circle at 50% 40%, ${equippedChar.palette.base}22, var(--surface-sunken))`, boxShadow: `inset 0 0 0 2px ${equippedChar.palette.base}55` }}
            >
              {equippedArt ? (
                <ArtImage src={equippedArt} alt={equippedChar.name} className="h-full w-full" fallbackIcon="user" fallbackColor={equippedChar.palette.base} />
              ) : (
                <span className="grid h-full w-full place-items-center"><PixelIcon name="user" size={24} /></span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold truncate">{user?.username}</div>
              <div className="text-xs text-[var(--text-dim)]">Playing as {equippedChar.name}</div>
              <div className="flex items-center gap-1 text-xs text-[var(--success)]">
                <PixelIcon name="wifi" size={12} />
                Synced to cloud
              </div>
            </div>
            <GameButton size="sm" variant="ghost" icon="arrow-left" onClick={logout}>
              Sign out
            </GameButton>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="rounded-[var(--r-lg)] bg-[var(--surface)] shadow-emboss border border-[var(--edge)]/40 p-4 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div
                className="relative h-14 w-14 shrink-0 overflow-hidden rounded-[var(--r-md)]"
                style={{ background: `radial-gradient(circle at 50% 40%, ${equippedChar.palette.base}22, var(--surface-sunken))`, boxShadow: `inset 0 0 0 2px ${equippedChar.palette.base}55` }}
              >
                {equippedArt ? (
                  <ArtImage src={equippedArt} alt={equippedChar.name} className="h-full w-full" fallbackIcon="user" fallbackColor={equippedChar.palette.base} />
                ) : (
                  <span className="grid h-full w-full place-items-center"><PixelIcon name="user" size={24} /></span>
                )}
              </div>
              <div className="min-w-0">
                <div className="font-display font-bold">Playing as {equippedChar.name}</div>
                <p className="text-xs text-[var(--text-dim)]">Sign in to sync {equippedChar.name} and your progress across devices.</p>
              </div>
            </div>
            <div className="flex gap-2">
              {(['login', 'register'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMode(m)}
                  className={['flex-1 rounded-full py-2 text-sm font-semibold', mode === m ? 'bg-[var(--ember)] text-[#231404]' : 'bg-[var(--surface-raised)] text-[var(--text-dim)]'].join(' ')}
                >
                  {m === 'login' ? 'Sign In' : 'Create'}
                </button>
              ))}
            </div>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              autoComplete="username"
              required
              minLength={3}
              className="rounded-[var(--r-sm)] bg-[var(--surface-sunken)] px-4 py-3 shadow-sunken outline-none placeholder:text-[var(--text-faint)]"
            />
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              type="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              minLength={6}
              className="rounded-[var(--r-sm)] bg-[var(--surface-sunken)] px-4 py-3 shadow-sunken outline-none placeholder:text-[var(--text-faint)]"
            />
            {error && <p className="text-sm text-[var(--danger)]">{error.replace(/_/g, ' ')}</p>}
            <GameButton type="submit" loading={busy} fullWidth icon="user">
              {mode === 'login' ? 'Sign In' : 'Create Account'}
            </GameButton>
            <p className="text-xs text-[var(--text-faint)] text-center">Progress saves offline and syncs when you sign in.</p>
          </form>
        )}

        <div className="grid grid-cols-3 gap-2">
          <Stat label="Total Stars" value={totalStars} />
          <Stat label="Grace Score" value={computeGraceScore(save)} />
          <Stat label="Levels" value={save.progress.completedLevels.length} />
          <Stat label="Coins" value={save.economy.coins} />
          <Stat label="Gems" value={save.economy.gems} />
          <Stat label="Achievements" value={`${save.meta.achievements.length}/${ACHIEVEMENTS.length}`} />
        </div>

        <div>
          <h2 className="font-display font-bold mb-2 text-[var(--text-dim)]">Achievements</h2>
          <div className="grid grid-cols-1 gap-2">
            {ACHIEVEMENTS.map((a) => {
              const earned = save.meta.achievements.includes(a.id)
              return (
                <div
                  key={a.id}
                  className={[
                    'flex items-center gap-3 rounded-[var(--r-md)] border p-3 transition',
                    earned
                      ? 'bg-[var(--surface)] border-[var(--ember)]/40 shadow-emboss'
                      : 'bg-[var(--surface-sunken)] border-[var(--edge)]/30 opacity-55',
                  ].join(' ')}
                >
                  <div
                    className="grid place-items-center w-10 h-10 rounded-[var(--r-sm)] shrink-0"
                    style={{
                      background: earned ? 'var(--surface-raised)' : 'transparent',
                      color: earned ? 'var(--coin)' : 'var(--text-faint)',
                    }}
                  >
                    <PixelIcon name={earned ? a.icon : 'lock'} size={20} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-bold text-sm">{a.name}</div>
                    <p className="text-xs text-[var(--text-dim)]">{a.description}</p>
                  </div>
                  {earned && (
                    <span className="shrink-0 text-[var(--success)]">
                      <PixelIcon name="success" size={18} />
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </ScreenShell>
  )
}
